'use client';

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, User, X, SlidersHorizontal, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Select from 'react-select';
import { useAuth } from '@/context/AuthContext';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { api } from '@/utils/api';
import NotificationsModalDesktop from '@/components/notifications/NotificationsModalDesktop';
import { useNotifications } from '@/hooks/useNotifications';
import {
    getUserImageUrl,
    getMolduraImageUrl,
    DEFAULT_AVATAR,
    avatarClipClassFromMolduraTipo,
} from '@/utils/image-utils';
import { LevelBadge } from '@/components/ranking/LevelBadge';
import { Input } from '@/components/ui/Input';
import { FORMATO_BUSCA_TODOS_QUERY } from '@/components/FormatoPicker';

export default function Navbar() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, user, logout } = useAuth();
    const { requireVipToAccess } = useSiteConfig();
    const { unreadCount, fetchUnreadCount } = useNotifications();
    const canAccessSite = !!user?.is_vip || !requireVipToAccess;


    const [mounted, setMounted] = useState(false);
    const [showNotificationsModal, setShowNotificationsModal] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [statuses, setStatuses] = useState<any[]>([]);
    const [tags, setTags] = useState<any[]>([]);
    const [loadingFilters, setLoadingFilters] = useState(false);
    const isSearchPage = pathname === '/pesquisar';
    const isEnPage = pathname === '/en' || pathname.startsWith('/en/');
    const lastScrollY = useRef(0);
    const lastSearchUrlRef = useRef<string>('');
    const isUpdatingSearchRef = useRef(false);

    // Alinhado à página /pesquisar (FormatoPicker + API `obras/buscar`)
    const FORMATOS_BUSCA_PADRAO = '5';

    // Estados dos filtros
    const currentStatus = searchParams.get('status') ? searchParams.get('status')!.split(',').map(Number) : [];
    const currentTags = searchParams.get('tags') ? searchParams.get('tags')!.split(',').map(Number) : [];
    const currentFormato = searchParams.get('formato') || FORMATOS_BUSCA_PADRAO;
    const formtIdForTags = (() => {
        const first = currentFormato.split(',')[0]?.trim();
        const n = Number(first);
        return Number.isInteger(n) && n > 0 ? n : 5;
    })();
    const currentMinCapitulos = Number(searchParams.get('minCapitulos')) || 0;
    const currentMaxCapitulos = Number(searchParams.get('maxCapitulos')) || 1000;
    const currentOrdenarPor = searchParams.get('ordenarPor') || 'criada_em_desc';

    // Check if user is VIP
    const isVip = !!user?.is_vip;

    useEffect(() => {
        setMounted(true);
    }, []);

    // Detectar scroll na página de pesquisar
    useEffect(() => {
        if (!isSearchPage) {
            setIsScrolled(false);
            return;
        }

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            // Se scrollar mais de 100px, ativar modo busca
            if (currentScrollY > 100) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
            lastScrollY.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isSearchPage]);

    // Inicializar lastSearchUrlRef quando entrar na página
    useEffect(() => {
        if (isSearchPage) {
            lastSearchUrlRef.current = searchParams.toString();
            const buscaFromUrl = searchParams.get('busca') || '';
            setSearchValue(buscaFromUrl);
        }
    }, [isSearchPage]);

    // Sincronizar searchValue com URL quando a URL mudar externamente (sem ser por nossa atualização)
    useEffect(() => {
        if (!isSearchPage || isUpdatingSearchRef.current) return;

        const currentUrl = searchParams.toString();
        const currentBusca = searchParams.get('busca') || '';

        // Verificar se a mudança foi na busca ou apenas na página
        const oldParams = new URLSearchParams(lastSearchUrlRef.current);
        const oldBusca = oldParams.get('busca') || '';

        // Só atualizar searchValue se a busca realmente mudou (não apenas a página)
        if (currentBusca !== oldBusca && currentUrl !== lastSearchUrlRef.current) {
            setSearchValue(currentBusca);
        }

        // Sempre atualizar a ref da URL para rastrear mudanças
        if (currentUrl !== lastSearchUrlRef.current) {
            lastSearchUrlRef.current = currentUrl;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearchPage, searchParams]);

    // Atualizar URL quando searchValue mudar (com debounce) - apenas quando o usuário digita na navbar
    useEffect(() => {
        if (!isSearchPage || !isScrolled || isUpdatingSearchRef.current) return;

        const timer = setTimeout(() => {
            // Usar searchParams diretamente aqui, não nas dependências
            const currentBusca = searchParams.get('busca') || '';

            // Só atualizar se o valor realmente mudou (usuário digitou algo diferente)
            if (searchValue !== currentBusca) {
                isUpdatingSearchRef.current = true;

                const params = new URLSearchParams(searchParams.toString());
                if (searchValue) {
                    params.set('busca', searchValue);
                } else {
                    params.delete('busca');
                }
                // Resetar página apenas quando a busca mudar
                params.set('pagina', '1');

                const newUrl = params.toString();
                // Só atualizar se a URL realmente mudou
                if (newUrl !== lastSearchUrlRef.current) {
                    lastSearchUrlRef.current = newUrl;
                    navigate(`/pesquisar?${newUrl}`);
                }

                setTimeout(() => {
                    isUpdatingSearchRef.current = false;
                }, 1000);
            }
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, isSearchPage, isScrolled]);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const params = new URLSearchParams(searchParams.toString());
            if (searchValue) {
                params.set('busca', searchValue);
            } else {
                params.delete('busca');
            }
            params.set('pagina', '1');
            navigate(`/pesquisar?${params.toString()}`);
        }
    };

    const clearSearch = () => {
        setSearchValue('');
        const params = new URLSearchParams(searchParams.toString());
        params.delete('busca');
        params.set('pagina', '1');
        navigate(`/pesquisar?${params.toString()}`);
    };

    // Buscar status e tags quando abrir o modal
    useEffect(() => {
        if (showFiltersModal && isSearchPage) {
            const fetchFilters = async () => {
                setLoadingFilters(true);
                try {
                    const [statusesRes, tagsRes] = await Promise.all([
                        api.get('obra-status'),
                        api.get(`tags?formt_id=${formtIdForTags}`)
                    ]);
                    setStatuses(Array.isArray(statusesRes) ? statusesRes : (statusesRes?.data || []));
                    setTags(Array.isArray(tagsRes) ? tagsRes : (tagsRes?.data || []));
                } catch (error) {
                    console.error('Erro ao buscar filtros:', error);
                } finally {
                    setLoadingFilters(false);
                }
            };
            fetchFilters();
        }
    }, [showFiltersModal, isSearchPage, currentFormato]);

    const handleFilterChange = (key: string, value: any) => {
        const params = new URLSearchParams(searchParams.toString());

        if (key === 'status') {
            if (value.length > 0) {
                params.set('status', value.join(','));
            } else {
                params.delete('status');
            }
        } else if (key === 'tags') {
            if (value.length > 0) {
                params.set('tags', value.join(','));
            } else {
                params.delete('tags');
            }
        } else if (key === 'formato') {
            params.set('formato', value);
            // Limpar tags quando mudar formato
            params.delete('tags');
        } else if (key === 'minCapitulos') {
            if (value > 0) {
                params.set('minCapitulos', value.toString());
            } else {
                params.delete('minCapitulos');
            }
        } else if (key === 'maxCapitulos') {
            if (value < 1000) {
                params.set('maxCapitulos', value.toString());
            } else {
                params.delete('maxCapitulos');
            }
        } else if (key === 'ordenarPor') {
            params.set('ordenarPor', value);
        }

        params.set('pagina', '1');
        navigate(`/pesquisar?${params.toString()}`);
    };

    const clearAllFilters = () => {
        const params = new URLSearchParams();
        if (searchValue) {
            params.set('busca', searchValue);
        }
        params.set('formato', FORMATOS_BUSCA_PADRAO);
        params.set('pagina', '1');
        navigate(`/pesquisar?${params.toString()}`);
    };

    const statusOptions = statuses.map(s => ({ value: s.id, label: s.nome }));
    const tagOptions = tags.map(t => ({ value: t.id, label: t.nome }));

    const transformValue = (ids: number[], options: any[]) => {
        return options.filter(opt => ids.includes(opt.value));
    };

    const customSelectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            background: '#171717',
            borderColor: state.isFocused ? '#f97316' : 'rgba(255, 255, 255, 0.05)',
            borderRadius: '0.75rem',
            padding: '0.25rem',
            boxShadow: 'none',
            '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.1)'
            }
        }),
        menu: (base: any) => ({
            ...base,
            background: '#171717',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '0.75rem',
            zIndex: 9999
        }),
        option: (base: any, state: any) => ({
            ...base,
            background: state.isSelected ? 'rgba(249, 115, 22, 0.2)' : state.isFocused ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
            color: state.isSelected ? '#f97316' : 'rgba(255, 255, 255, 0.8)',
            cursor: 'pointer',
            ':active': {
                backgroundColor: 'rgba(249, 115, 22, 0.3)'
            }
        }),
        multiValue: (base: any) => ({
            ...base,
            background: 'rgba(249, 115, 22, 0.1)',
            borderRadius: '0.375rem',
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: '#f97316',
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            color: '#f97316',
            ':hover': {
                background: 'rgba(249, 115, 22, 0.2)',
                color: '#c2410c',
            },
        }),
        input: (base: any) => ({ ...base, color: 'white' }),
        singleValue: (base: any) => ({ ...base, color: 'white' }),
        placeholder: (base: any) => ({ ...base, color: 'rgba(255, 255, 255, 0.4)' }),
    };

    const activeFilterCount = currentStatus.length + currentTags.length + (currentMinCapitulos > 0 ? 1 : 0) + (currentMaxCapitulos < 1000 ? 1 : 0);

    // Não mostrar navbar em páginas de leitura
    if (pathname.startsWith('/capitulo/')) {
        return null;
    }

    // Links que só aparecem para usuários VIP autenticados
    const vipNavItems = [
        { href: '/inicio', label: 'Início' },
        { href: '/colecao', label: 'Coleção' },
        { href: '/listas', label: 'Listas' },
        { href: '/ranking', label: 'Ranking' },
        { href: '/historico', label: 'Histórico' },
        { href: '/comunidade', label: 'Comunidade' },
        { href: '/planos', label: 'Planos' },
    ];

    // Se estiver na página de pesquisar e scrollado, mostrar barra de busca
    if (isSearchPage && isScrolled) {
        return (
            <>
                <nav className="hidden lg:block fixed top-0 w-full z-50 bg-neutral-950/95 backdrop-blur-md border-b border-white/5 pwa-navbar transition-all duration-300">
                    <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center gap-4">
                        {/* Input de Busca */}
                        <div className="flex-1 relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/40 group-focus-within:text-orange-500 transition-colors">
                                <Search size={22} />
                            </div>
                            <Input
                                type="text"
                                placeholder="Buscar por nome..."
                                className="rounded-xl !border-white/5 bg-neutral-900 py-3.5 pl-12 pr-10 text-base focus:bg-neutral-900/80"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                autoFocus
                            />
                            {searchValue && (
                                <button
                                    type="button"
                                    onClick={clearSearch}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/40 hover:text-white transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>

                        {/* Botão de Filtro */}
                        <button
                            onClick={() => setShowFiltersModal(true)}
                            className={`relative px-4 py-3 bg-neutral-900 border rounded-xl flex items-center gap-2 text-white/70 hover:text-white hover:border-orange-500/50 transition-all ${activeFilterCount > 0 ? 'border-orange-500/50 text-orange-500' : 'border-white/5'
                                }`}
                        >
                            <SlidersHorizontal size={20} />
                            <span className="text-sm font-medium">Filtros</span>
                            {activeFilterCount > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>

                        {/* Botão de voltar ao topo */}
                        <button
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className="px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-all text-sm font-medium"
                        >
                            Voltar
                        </button>
                    </div>
                </nav>

                {/* Modal de Filtros */}
                <AnimatePresence>
                    {showFiltersModal && (
                        <>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
                                onClick={() => setShowFiltersModal(false)}
                            />
                            <motion.div
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.2 }}
                                className="fixed top-24 left-1/2 -translate-x-1/2 z-[201] bg-neutral-900 border border-white/10 rounded-2xl shadow-2xl max-h-[calc(100vh-8rem)] w-[90vw] max-w-2xl flex flex-col overflow-hidden"
                                onClick={(e: any) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                                    <h2 className="text-xl font-bold text-white">Filtros</h2>
                                    <button
                                        onClick={() => setShowFiltersModal(false)}
                                        className="p-2 hover:bg-white/5 rounded-lg transition-colors text-white/40 hover:text-white"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                                    {loadingFilters ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                                        </div>
                                    ) : (
                                        <>
                                            {/* Status */}
                                            <div>
                                                <h3 className="text-sm font-bold text-white/40 uppercase mb-3">Status</h3>
                                                <Select
                                                    isMulti
                                                    options={statusOptions}
                                                    value={transformValue(currentStatus, statusOptions)}
                                                    onChange={(val: any) => handleFilterChange('status', val.map((v: any) => v.value))}
                                                    styles={customSelectStyles}
                                                    placeholder="Selecione o status..."
                                                    noOptionsMessage={() => "Nenhum status encontrado"}
                                                />
                                            </div>

                                            {/* Gêneros */}
                                            <div>
                                                <h3 className="text-sm font-bold text-white/40 uppercase mb-3">Gêneros</h3>
                                                <Select
                                                    isMulti
                                                    options={tagOptions}
                                                    value={transformValue(currentTags, tagOptions)}
                                                    onChange={(val: any) => handleFilterChange('tags', val.map((v: any) => v.value))}
                                                    styles={customSelectStyles}
                                                    placeholder="Selecione os gêneros..."
                                                    noOptionsMessage={() => "Nenhum gênero encontrado"}
                                                />
                                            </div>

                                            {/* Capítulos */}
                                            <div>
                                                <h3 className="text-sm font-bold text-white/40 uppercase mb-3">Capítulos</h3>
                                                <div className="bg-neutral-900/50 border border-white/5 rounded-xl p-4 space-y-4">
                                                    <div>
                                                        <div className="flex justify-between text-xs text-white/60 mb-2">
                                                            <span>Mín: {currentMinCapitulos}</span>
                                                        </div>
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="1000"
                                                            step="10"
                                                            value={currentMinCapitulos}
                                                            onChange={(e) => handleFilterChange('minCapitulos', Number(e.target.value))}
                                                            className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-orange-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="flex justify-between text-xs text-white/60 mb-2">
                                                            <span>Max: {currentMaxCapitulos === 1000 ? '1000+' : currentMaxCapitulos}</span>
                                                        </div>
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="1000"
                                                            step="10"
                                                            value={currentMaxCapitulos}
                                                            onChange={(e) => handleFilterChange('maxCapitulos', Number(e.target.value))}
                                                            className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-orange-500"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Ordenar Por */}
                                            <div>
                                                <h3 className="text-sm font-bold text-white/40 uppercase mb-3">Ordenar Por</h3>
                                                <select
                                                    value={currentOrdenarPor}
                                                    onChange={(e) => handleFilterChange('ordenarPor', e.target.value)}
                                                    className="w-full bg-neutral-900 border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 cursor-pointer"
                                                >
                                                    <option value="criada_em_desc">Mais Recentes</option>
                                                    <option value="view_geral">Mais Populares</option>
                                                    <option value="nome">Ordem Alfabética</option>
                                                </select>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-white/5 flex justify-between items-center">
                                    <button
                                        onClick={clearAllFilters}
                                        className="px-4 py-2 border border-white/10 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors text-sm font-medium flex items-center gap-2"
                                    >
                                        <X size={16} />
                                        Limpar Filtros
                                    </button>
                                    <button
                                        onClick={() => setShowFiltersModal(false)}
                                        className="px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors text-sm font-medium"
                                    >
                                        Aplicar
                                    </button>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </>
        );
    }

    return (
        <nav className="hidden lg:block fixed top-0 w-full z-50 bg-neutral-950/80 backdrop-blur-md border-b border-white/5 pwa-navbar transition-all duration-300 navbar-with-banner">
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center gap-4">
                {/* Logo — à esquerda */}
                <Link
                    to={isAuthenticated && canAccessSite ? '/' : isAuthenticated ? '/landing' : '/'}
                    className="flex shrink-0 items-center gap-4 group"
                >
                    <div className="relative w-12 h-12 rounded-full overflow-hidden flex items-center justify-center">
                        <img
                            src={isEnPage ? '/logoen.png' : '/favicon.png'}
                            alt="MediocreToons Logo"
                            className="object-contain w-12 h-12"
                        />
                    </div>
                </Link>

                {/* Restante — alinhado à direita */}
                <div className="flex min-w-0 flex-1 items-center justify-end gap-3 md:gap-4">
                    {/* Links VIP - aparecem se usuário pode acessar o site (VIP ou config desativada) */}
                    {mounted && isAuthenticated && canAccessSite && vipNavItems.map((item) => {
                        const isActive = pathname === item.href;

                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                className={`
                                    relative px-3 py-2 rounded-md transition-all text-sm font-medium sm:text-[15px]
                                    ${isActive
                                        ? 'text-orange-500'
                                        : 'text-white/70 hover:text-white hover:bg-white/5'
                                    }
                                `}
                            >
                                <span>{item.label}</span>
                                {isActive && (
                                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-orange-500 rounded-full" />
                                )}
                            </Link>
                        );
                    })}

                    {/* Se não pode acessar (exige VIP e não tem), mostrar Perfil, VIP e Sair */}
                    {mounted && isAuthenticated && !canAccessSite && (
                        <>
                            <Link
                                to="/planos"
                                className="relative px-3 py-2 rounded-md transition-all text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 sm:text-[15px]"
                            >
                                Planos
                            </Link>

                            <Link
                                to="/meu-perfil"
                                className="relative px-3 py-2 rounded-md transition-all text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 sm:text-[15px]"
                            >
                                Perfil
                            </Link>

                            <button
                                type="button"
                                onClick={() => logout()}
                                className="relative px-3 py-2 rounded-md transition-all text-sm font-medium text-white/70 hover:text-red-400 hover:bg-white/5 sm:text-[15px]"
                                title="Sair"
                            >
                                Sair
                            </button>
                        </>
                    )}

                    {/* Pesquisar — à esquerda das notificações */}
                    {mounted && isAuthenticated && canAccessSite && (
                        <Link
                            to={`/pesquisar?formato=${encodeURIComponent(FORMATO_BUSCA_TODOS_QUERY)}&pagina=1`}
                            className={`
                                relative flex shrink-0 items-center gap-2 px-3 py-2 rounded-md transition-all text-sm font-medium sm:text-[15px]
                                ${pathname === '/pesquisar'
                                    ? 'text-orange-500'
                                    : 'text-white/70 hover:text-white hover:bg-white/5'
                                }
                            `}
                            title="Pesquisar"
                        >
                            <Search size={18} strokeWidth={2} className="shrink-0" />
                            <span>Pesquisar</span>
                            {pathname === '/pesquisar' && (
                                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-orange-500 rounded-full" />
                            )}
                        </Link>
                    )}

                    {/* Notificações (ícone) — à esquerda do perfil */}
                    {mounted && isAuthenticated && canAccessSite && (
                        <button
                            type="button"
                            onClick={() => setShowNotificationsModal(true)}
                            className={`
                                relative flex shrink-0 items-center justify-center w-9 h-9 rounded-md transition-all
                                ${showNotificationsModal
                                    ? 'text-orange-500 bg-white/5'
                                    : 'text-white/70 hover:text-white hover:bg-white/5'
                                }
                            `}
                            title="Notificações"
                        >
                            <Bell size={18} strokeWidth={2} />
                            {unreadCount > 0 && (
                                <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full border-2 border-neutral-950 bg-orange-500" />
                            )}
                        </button>
                    )}

                    {/* Perfil quando pode acessar */}
                    {mounted && isAuthenticated && canAccessSite && (
                        <Link
                            to="/meu-perfil"
                            className="group flex min-w-0 items-center gap-2.5 overflow-visible"
                        >
                            <div className="relative shrink-0 pb-2">
                                <div className="relative">
                                    {user?.moldura?.mol_imagem && (
                                        <div className="absolute -inset-1 z-20 pointer-events-none">
                                            <img
                                                src={getMolduraImageUrl(user.moldura.mol_imagem) || ''}
                                                alt=""
                                                className="w-[100%] h-[100%] max-w-none max-h-none -left-[20%] -top-[20%]"
                                            />
                                        </div>
                                    )}
                                    <div
                                        className={`relative z-10 flex h-8 w-8 items-center justify-center overflow-hidden border border-white/10 bg-neutral-800 ${avatarClipClassFromMolduraTipo(user?.moldura?.mol_tipo)}`}
                                    >
                                        <div className="absolute inset-0 flex items-center justify-center bg-neutral-700">
                                            <User size={15} className="text-white/60" />
                                        </div>
                                        <img
                                            src={getUserImageUrl(user?.usr_id || '', user?.usr_imagem)}
                                            alt={user?.usr_nick || 'Avatar'}
                                            className="relative z-[1] h-full w-full object-cover"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                                            }}
                                        />
                                    </div>
                                    <div className="absolute -bottom-1 left-1/2 z-30 min-w-max -translate-x-1/2">
                                        <LevelBadge
                                            level={user?.nivel ?? user?.usr_nivel ?? 1}
                                            size="sm"
                                            className="shadow-black/50"
                                        />
                                    </div>
                                </div>
                            </div>
                            <span className="max-w-[100px] truncate text-sm font-medium text-white transition-colors group-hover:text-orange-500 sm:max-w-[120px] sm:text-[15px]">
                                {user?.usr_nick}
                            </span>
                        </Link>
                    )}

                    {/* Login */}
                    {(!isAuthenticated || !mounted) && (
                        <Link
                            to="/entrar"
                            className="flex shrink-0 items-center rounded-lg bg-white px-4 py-2 text-sm font-medium text-black transition-all hover:bg-gray-200 sm:text-[15px]"
                        >
                            Entrar
                        </Link>
                    )}
                </div>

            </div>

            <NotificationsModalDesktop isOpen={showNotificationsModal} onClose={() => {
                setShowNotificationsModal(false);
                fetchUnreadCount();
            }} />
        </nav>
    );
}
